import { useMemo } from 'react';
import { Box, Grid } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Label } from 'components/elements/Label';
import { Text } from 'components/elements/Text';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { useBusinessLicence } from 'hooks/useBusinessLicense';
import { useBusinessLicenceV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { trackProjectMaterials } from 'utils/analytics';
import { parseRelatedContactsIntoList } from 'utils/contacts';

export const BusinessLicense = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result: any;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = useBusinessLicenceV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('businessLicense');
    resolvedDocumentId = documentUuid || undefined;
    result = useBusinessLicence(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const permitUrl = result.data?.document_url;

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Business Licence">
          {result.data?.metadata.permitnum}
        </ModalHeader>
      }
    >
      <Box>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address ?? ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContacts}
          companies={result.data?.companies}
          applicant={result?.data?.metadata.applicant}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />
        <ModalStatus Skeleton={Skeleton} activities={result.data?.activities} />
        <InfoModal.Row>
          <Label>License Type</Label>
          <InfoModal.RowDetails>
            <Grid templateColumns="1fr auto" gridGap={3} width="100%">
              <Skeleton variant="text" noOfLines={1}>
                {result.data?.metadata.description ? (
                  <Text textTransform="uppercase">
                    {result.data?.metadata.description}
                  </Text>
                ) : (
                  <EmptyField />
                )}
              </Skeleton>
            </Grid>
          </InfoModal.RowDetails>
        </InfoModal.Row>

        {result.isLoading && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <Skeleton variant="text" noOfLines={1} />
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}

        {!result.isLoading && permitUrl && (
          <InfoModal.Row>
            <Label>Relevant Materials</Label>
            <InfoModal.RowDetails>
              <ExternalLink
                to={permitUrl}
                onClick={() => trackProjectMaterials(result.data?.document_url || '')}
              >
                View application
              </ExternalLink>
            </InfoModal.RowDetails>
          </InfoModal.Row>
        )}
      </Box>
    </InfoModal>
  );
};
